import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

type WatchlistSidebarCollapseState = {
  isCollapsed: boolean;
  toggle: () => void;
  setCollapsed: (collapsed: boolean) => void;
};

const WatchlistSidebarCollapseContext = createContext<WatchlistSidebarCollapseState | null>(null);

/**
 * Get the storage key based on current screen width
 * - Large screens (>= 1024px): watchlist-sidebar-collapsed-lg
 * - Medium screens (768-1023px): watchlist-sidebar-collapsed-md
 */
const getStorageKey = (): string => {
  const isLargeScreen = typeof window !== "undefined" && window.innerWidth >= 1024;
  return isLargeScreen ? "watchlist-sidebar-collapsed-lg" : "watchlist-sidebar-collapsed-md";
};

/**
 * Provider component to manage WatchlistSidebar collapse state
 * Uses separate localStorage keys for different screen sizes
 */
export const WatchlistSidebarCollapseProvider = ({ children }: { children: React.ReactNode }) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    const key = getStorageKey();
    const isLargeScreen = typeof window !== "undefined" && window.innerWidth >= 1024;
    const screenBasedDefault = !isLargeScreen;

    try {
      const stored = localStorage.getItem(key);
      if (stored === null) {
        return screenBasedDefault;
      }
      return stored === "true";
    } catch {
      return screenBasedDefault;
    }
  });

  // Listen for window resize and update state when crossing 1024px boundary
  useEffect(() => {
    let lastScreenType = window.innerWidth >= 1024 ? "lg" : "md";

    const handleResize = () => {
      const currentScreenType = window.innerWidth >= 1024 ? "lg" : "md";

      // Only update when screen type changes (crossing 1024px boundary)
      if (currentScreenType !== lastScreenType) {
        lastScreenType = currentScreenType;

        const key = getStorageKey();
        const isLargeScreen = currentScreenType === "lg";
        const screenBasedDefault = !isLargeScreen;

        try {
          const stored = localStorage.getItem(key);
          if (stored === null) {
            // No stored value for this screen size, use screen-based default
            setIsCollapsed(screenBasedDefault);
          } else {
            // Use stored value for this screen size
            const newValue = stored === "true";
            setIsCollapsed(newValue);
          }
        } catch {
          setIsCollapsed(screenBasedDefault);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggle = useCallback(() => {
    setIsCollapsed((prev) => {
      const newValue = !prev;
      // Use current screen size to determine which key to write
      const key = getStorageKey();
      try {
        localStorage.setItem(key, String(newValue));
      } catch {
        console.error("[WatchlistSidebarCollapseProvider] toggle: localStorage unavailable");
      }
      return newValue;
    });
  }, [isCollapsed]);

  const setCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsed(() => {
      // Use current screen size to determine which key to write
      const key = getStorageKey();
      try {
        localStorage.setItem(key, String(collapsed));
      } catch {
        console.error("[WatchlistSidebarCollapseProvider] setCollapsed: localStorage unavailable");
      }
      return collapsed;
    });
  }, []);

  // Memoize the state object to prevent unnecessary re-renders
  const sidebarState = useMemo(() => ({ isCollapsed, toggle, setCollapsed }), [isCollapsed, toggle, setCollapsed]);

  return <WatchlistSidebarCollapseContext.Provider value={sidebarState}>{children}</WatchlistSidebarCollapseContext.Provider>;
};

/**
 * Hook to manage WatchlistSidebar collapse state with localStorage persistence
 * Each screen size has its own stored preference
 */
export const useWatchlistSidebarCollapse = (): WatchlistSidebarCollapseState => {
  const context = useContext(WatchlistSidebarCollapseContext);

  if (!context) {
    console.error("WatchlistSidebarCollapseContext not found. Make sure App.tsx wraps children with WatchlistSidebarCollapseProvider.");
    throw new Error("useWatchlistSidebarCollapse must be used within WatchlistSidebarCollapseProvider");
  }

  return context;
};
